import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import { Button, IconButton } from '../../components/ui/primitives';
import styles from './create.module.css';

const MAX_SECONDS = 60;

function pickMime(): string {
  const candidates = ['video/mp4;codecs=avc1,mp4a', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return candidates.find((c) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) ?? '';
}

/** In-app camera recorder (MediaRecorder). Produces a Blob that goes through the normal upload flow. */
export function Recorder({ onDone, onCancel }: { onDone: (file: File) => void; onCancel: () => void }) {
  const preview = useRef<HTMLVideoElement>(null);
  const stream = useRef<MediaStream | null>(null);
  const recorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const [facing, setFacing] = useState<'user' | 'environment'>('user');
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        stream.current?.getTracks().forEach((t) => t.stop());
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: { ideal: 1080 }, height: { ideal: 1920 }, aspectRatio: { ideal: 9 / 16 } },
          audio: true,
        });
        if (cancelled) return s.getTracks().forEach((t) => t.stop());
        stream.current = s;
        if (preview.current) {
          preview.current.srcObject = s;
          await preview.current.play().catch(() => {});
        }
        setError(null);
      } catch {
        setError('Geen toegang tot camera of microfoon. Geef toestemming in je browser of upload een video.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [facing]);

  useEffect(() => () => stream.current?.getTracks().forEach((t) => t.stop()), []);

  useEffect(() => {
    if (!recording) return;
    const started = Date.now();
    const t = setInterval(() => {
      const s = (Date.now() - started) / 1000;
      setElapsed(s);
      if (s >= MAX_SECONDS) stop();
    }, 100);
    return () => clearInterval(t);
  }, [recording]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    if (!stream.current) return;
    const mimeType = pickMime();
    const rec = new MediaRecorder(stream.current, mimeType ? { mimeType, videoBitsPerSecond: 4_000_000 } : undefined);
    chunks.current = [];
    rec.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    rec.onstop = () => {
      const type = rec.mimeType || 'video/webm';
      const blob = new Blob(chunks.current, { type });
      stream.current?.getTracks().forEach((t) => t.stop());
      onDone(new File([blob], `opname.${type.includes('mp4') ? 'mp4' : 'webm'}`, { type }));
    };
    rec.start(250);
    recorder.current = rec;
    setElapsed(0);
    setRecording(true);
  };

  const stop = () => {
    if (recorder.current?.state === 'recording') recorder.current.stop();
    setRecording(false);
  };

  return (
    <div className={styles.recorder}>
      <video ref={preview} className={styles.recPreview} muted playsInline style={{ transform: facing === 'user' ? 'scaleX(-1)' : undefined }} />
      <div className={styles.recTop}>
        <IconButton icon="close" label="Annuleren" onClick={onCancel} />
        {recording && <span className={styles.recTimer}>● {Math.floor(elapsed)}s / {MAX_SECONDS}s</span>}
        <IconButton icon="refresh" label="Camera wisselen" onClick={() => setFacing((f) => (f === 'user' ? 'environment' : 'user'))} disabled={recording} />
      </div>
      {error ? (
        <div className={styles.recError}>
          <p>{error}</p>
          <Button variant="secondary" onClick={onCancel}>
            Terug
          </Button>
        </div>
      ) : (
        <div className={styles.recBottom}>
          <div className={styles.recProgress}>
            <span style={{ transform: `scaleX(${elapsed / MAX_SECONDS})` }} />
          </div>
          <button className={`${styles.recButton} ${recording ? styles.recOn : ''}`} onClick={recording ? stop : start} aria-label={recording ? 'Stop opname' : 'Start opname'}>
            <Icon name={recording ? 'stop' : 'record'} size={recording ? 28 : 56} />
          </button>
        </div>
      )}
    </div>
  );
}
